import axiosInstance from '@/lib/enhancedAxiosInstance';
import { API_ENDPOINTS } from '@/config/config';
import { logError, logDebug } from '@/utils/errorHandler';

// Enterprise Analytics API endpoints
const ANALYTICS_URL = '/api/analytics';

// Define types for dashboard data
export interface DashboardSummary {
  total_products: number;
  inactive_product_count: number;
  data_completeness: number;
  most_missing_fields: { attribute_label: string; missing_count: number }[];
  active_products: number;
  inactive_products: number;
  attributes_missing_count: number;
  mandatory_attributes: string[];
  recent_products: {
    id: number;
    name: string;
    sku: string;
    family: { name: string };
  }[];
  recent_activity: Activity[];
  incomplete_products: IncompleteProduct[];
  completeness_trend: CompletenessTrendData;
  family_completeness: FamilyCompleteness[];
  isNewOrganization?: boolean;
  message?: string;
}

export interface InventoryTrend {
  dates: string[];
  values: number[];
}

export interface FamilyCompleteness {
  familyName: string;
  percentage: number;
}

export interface Activity {
  id: number;
  user_name: string;
  entity: string;
  entity_id: number;
  action: 'create' | 'update' | 'delete';
  message: string;
  created_at: string;
}

// Detail for a single missing field
export interface FieldDetail {
  field: string; // e.g., "Name", "SKU", "Attribute: Color"
  weight: number;
  attribute_id?: number;
  attribute_code?: string;
  attribute_type?: string; // e.g., 'text', 'number', 'boolean', 'select', 'date'
}

// Entry for the field_completeness array
export interface FieldCompletenessEntry extends FieldDetail {
  complete: boolean;
}

export interface IncompleteProduct {
  id: number;
  name: string;
  sku: string;
  completeness: number;
  family?: { name: string };
  missing_fields: string[]; // Changed from FieldDetail[] to string[]
  missing_fields_count: number; // New field for counting missing fields
  field_completeness: FieldCompletenessEntry[]; // Comprehensive list of all tracked fields and their status
}

// Define interface for incomplete products parameters
export interface IncompleteProductsParams {
  organization_id: number;
  limit?: number;
  product_id?: number;
}

// Define path to dashboard endpoints - use the path from config
const DASHBOARD_URL = API_ENDPOINTS.dashboard;

/**
 * Build URL with organization ID parameter
 * @param endpoint Base endpoint URL
 * @param organizationId Organization ID
 * @returns URL with organization_id parameter
 */
const withOrgParam = (endpoint: string, organizationId?: string): string => {
  if (!organizationId) return endpoint;

  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}organization_id=${organizationId}`;
};

/**
 * Fetch dashboard summary data from enterprise analytics system
 *
 * This function exclusively uses the precomputed analytics tables for blazing fast performance.
 * No fallback to legacy endpoints - analytics system is the primary and only data source.
 *
 * @param organizationId Optional organization ID to filter data (auto-detected from user if not provided)
 */
export const getDashboardSummary = async (organizationId?: string): Promise<DashboardSummary> => {
  try {
    const token = localStorage.getItem('access_token');
    const analyticsUrl = `${ANALYTICS_URL}/dashboard_summary/`;

    // Log API call for debugging (development only)
    logDebug('Dashboard API call initiated', {
      url: analyticsUrl,
      hasToken: !!token,
      organizationId: organizationId || 'auto-detected',
    });

    const analyticsResponse = await axiosInstance.get(analyticsUrl);

    // Log successful response for debugging (development only)
    logDebug('Dashboard API response received', {
      status: analyticsResponse.status,
      dataType: typeof analyticsResponse.data,
      source: 'precomputed_analytics',
    });

    return analyticsResponse.data;
  } catch (error) {
    logError(
      'Failed to load dashboard summary',
      {
        component: 'dashboardService',
        action: 'getDashboardSummary',
        organizationId,
        metadata: { analyticsUrl: `${ANALYTICS_URL}/dashboard_summary/` },
      },
      {
        showToast: false,
        userFacing: false,
      }
    );
    throw error;
  }
};

/**
 * Fetch recent activity data
 * @param organizationId Optional organization ID to filter data
 */
export const getRecentActivity = async (organizationId?: string): Promise<Activity[]> => {
  try {
    const token = localStorage.getItem('access_token');
    const url = withOrgParam(`${DASHBOARD_URL}/activity/`, organizationId);

    // Log API call for debugging (development only)
    logDebug('Recent activity API call initiated', {
      hasToken: !!token,
      organizationId: organizationId || 'not provided',
    });

    const response = await axiosInstance.get(url);

    // Log response for debugging (development only)
    logDebug('Activity API response received', {
      status: response.status,
      isArray: Array.isArray(response.data),
      dataType: typeof response.data,
    });

    // Ensure we always return an array
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    logError(
      'Failed to load recent activity',
      {
        component: 'dashboardService',
        action: 'getRecentActivity',
        organizationId,
        metadata: { endpoint: `${DASHBOARD_URL}/activity/` },
      },
      {
        showToast: false,
        userFacing: false,
      }
    );
    throw error;
  }
};

/**
 * Fetch incomplete products with optional filtering parameters
 * @param params Object containing organization_id (required), limit (optional), and product_id (optional)
 * @returns Array of incomplete products or a single product when product_id is provided
 */
export const getIncompleteProducts = async (
  params: IncompleteProductsParams | string
): Promise<IncompleteProduct[]> => {
  try {
    let url = `${DASHBOARD_URL}/incomplete-products/`;
    // Handle both legacy string parameter and new params object
    if (typeof params === 'string') {
      // Legacy support - organizationId as string parameter
      url = withOrgParam(url, params);
      logDebug('Legacy parameter format used for incomplete products', {
        organizationId: params,
      });
    } else {
      // New parameter object format
      const { organization_id, limit, product_id } = params;
      url = `${url}?organization_id=${organization_id}`;
      if (limit !== undefined) {
        url += `&limit=${limit}`;
      }
      if (product_id !== undefined) {
        url += `&product_id=${product_id}`;
      }
    }
    const response = await axiosInstance.get(url);
    // If product_id is provided, the response might be a single object
    if (!Array.isArray(response.data) && typeof response.data === 'object') {
      return [response.data];
    }
    // Ensure we always return an array
    return Array.isArray(response.data) ? response.data : [];
  } catch {
    logError(
      'Failed to load incomplete products',
      {
        component: 'dashboardService',
        action: 'getIncompleteProducts',
        metadata: { params },
      },
      {
        showToast: false,
        userFacing: false,
      }
    );
    return [];
  }
};

/**
 * Completeness trend data structure
 */
export interface CompletenessTrendData {
  dates: string[];
  completeness: number[];
  delta: number;
  current: number;
  trend_direction: 'up' | 'down' | 'stable';
}

/**
 * Get completeness trend data from enterprise analytics system
 *
 * This function exclusively uses the precomputed analytics tables for optimal performance.
 * No fallback to legacy endpoints - analytics system is the primary and only data source.
 *
 * @param days Number of days to include (default: 30, max: 90)
 * @returns Promise resolving to completeness trend data
 */
export async function getCompletenessTrend(days: number = 30): Promise<CompletenessTrendData> {
  try {
    const analyticsResponse = await axiosInstance.get<CompletenessTrendData>(
      `${ANALYTICS_URL}/completeness_trend/?days=${Math.min(days, 90)}`
    );

    // Log successful response for debugging (development only)
    logDebug('Completeness trend API response received', {
      status: analyticsResponse.status,
      dataType: typeof analyticsResponse.data,
      source: 'precomputed_analytics',
    });

    return analyticsResponse.data;
  } catch (error) {
    logError(
      'Failed to load completeness trend',
      {
        component: 'dashboardService',
        action: 'getCompletenessTrend',
        metadata: { days },
      },
      {
        showToast: false,
        userFacing: false,
      }
    );
    throw error;
  }
}

// ============================================================================
// ENTERPRISE ANALYTICS FUNCTIONS
// ============================================================================

/**
 * Get family completeness data from enterprise analytics
 * @param limit Number of families to return (default: 10)
 */
export const getFamilyCompleteness = async (limit: number = 10): Promise<FamilyCompleteness[]> => {
  try {
    const response = await axiosInstance.get(
      `${ANALYTICS_URL}/family_completeness/?limit=${limit}`
    );
    return response.data;
  } catch (error) {
    logError(
      'Failed to load family completeness data',
      {
        component: 'dashboardService',
        action: 'getFamilyCompleteness',
        metadata: { limit },
      },
      {
        showToast: false,
        userFacing: false,
      }
    );
    throw error;
  }
};

/**
 * Get most missing fields data from enterprise analytics
 * @param limit Number of fields to return (default: 10)
 */
export const getMostMissingFields = async (limit: number = 10) => {
  try {
    const response = await axiosInstance.get(
      `${ANALYTICS_URL}/most_missing_fields/?limit=${limit}`
    );
    return response.data;
  } catch (error) {
    logError(
      'Failed to load most missing fields data',
      {
        component: 'dashboardService',
        action: 'getMostMissingFields',
        metadata: { limit },
      },
      {
        showToast: false,
        userFacing: false,
      }
    );
    throw error;
  }
};

/**
 * Get recent activity from enterprise analytics
 * @param limit Number of activities to return (default: 10)
 */
export const getRecentActivityFromAnalytics = async (limit: number = 10): Promise<Activity[]> => {
  try {
    const response = await axiosInstance.get(`${ANALYTICS_URL}/recent_activity/?limit=${limit}`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    logError(
      'Failed to load recent activity from analytics',
      {
        component: 'dashboardService',
        action: 'getRecentActivityFromAnalytics',
        metadata: { limit },
      },
      {
        showToast: false,
        userFacing: false,
      }
    );
    throw error;
  }
};

/**
 * Get recent products from enterprise analytics
 * @param limit Number of products to return (default: 5)
 */
export const getRecentProductsFromAnalytics = async (limit: number = 5) => {
  try {
    const response = await axiosInstance.get(`${ANALYTICS_URL}/recent_products/?limit=${limit}`);
    return response.data;
  } catch (error) {
    logError(
      'Failed to load recent products from analytics',
      {
        component: 'dashboardService',
        action: 'getRecentProductsFromAnalytics',
        metadata: { limit },
      },
      {
        showToast: false,
        userFacing: false,
      }
    );
    throw error;
  }
};

/**
 * Get incomplete products from enterprise analytics
 * @param limit Number of products to return (default: 20)
 */
export const getIncompleteProductsFromAnalytics = async (
  limit: number = 20
): Promise<IncompleteProduct[]> => {
  try {
    const response = await axiosInstance.get(
      `${ANALYTICS_URL}/incomplete_products/?limit=${limit}`
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    logError(
      'Failed to load incomplete products from analytics',
      {
        component: 'dashboardService',
        action: 'getIncompleteProductsFromAnalytics',
        metadata: { limit },
      },
      {
        showToast: false,
        userFacing: false,
      }
    );
    throw error;
  }
};

/**
 * Get analytics refresh status and health information
 */
export const getAnalyticsRefreshStatus = async () => {
  try {
    const response = await axiosInstance.get(`${ANALYTICS_URL}/refresh_status/`);
    return response.data;
  } catch (error) {
    logError(
      'Failed to load analytics refresh status',
      {
        component: 'dashboardService',
        action: 'getAnalyticsRefreshStatus',
      },
      {
        showToast: false,
        userFacing: false,
      }
    );
    throw error;
  }
};
